/**
 * Display formatting for numbers, masses, percentages, money and dates.
 *
 * Inputs are the decimal strings the API sends. They go straight to `Intl.NumberFormat`, which
 * formats numeric strings exactly (no float conversion), so "0.1235" at 3 decimals is "0.124"
 * and never "0.123". Missing values render as an em dash, never as zero. Negative numbers use
 * the true minus sign (U+2212), per the typography rules.
 */
import { Decimal, type DecimalInput } from "./decimal";

export type NumericInput = DecimalInput | null | undefined;

/** Shown for missing values. Missing composition data is not zero (project standing decision). */
export const MISSING_VALUE = "—";
const MINUS_SIGN = "\u2212";

const FALLBACK_LOCALE = "en-CA";

/** The browser's preferred locale (George's default is Canada). */
export function defaultLocale(): string {
  if (typeof navigator !== "undefined") {
    return navigator.languages?.[0] ?? navigator.language ?? FALLBACK_LOCALE;
  }
  return FALLBACK_LOCALE;
}

const formatterCache = new Map<string, Intl.NumberFormat>();

function numberFormatter(locale: string, options: Intl.NumberFormatOptions): Intl.NumberFormat {
  const key = `${locale}|${JSON.stringify(options)}`;
  let formatter = formatterCache.get(key);
  if (!formatter) {
    formatter = new Intl.NumberFormat(locale, options);
    formatterCache.set(key, formatter);
  }
  return formatter;
}

function formatParts(value: Decimal, locale: string, options: Intl.NumberFormatOptions): string {
  return numberFormatter(locale, options)
    .formatToParts(value.toString() as Intl.StringNumericLiteral)
    .map((part) => (part.type === "minusSign" ? MINUS_SIGN : part.value))
    .join("");
}

export interface NumberFormatOptions {
  /** Fixed number of decimal places (sets both minimum and maximum). */
  decimals?: number;
  minDecimals?: number;
  maxDecimals?: number;
  /** "exceptZero" shows "+" on positive deltas. */
  signDisplay?: "auto" | "always" | "exceptZero" | "never";
  grouping?: boolean;
  locale?: string;
}

function toIntlOptions(options: NumberFormatOptions, fallbackDecimals: [number, number]): Intl.NumberFormatOptions {
  const minimumFractionDigits = options.decimals ?? options.minDecimals ?? fallbackDecimals[0];
  const maximumFractionDigits = Math.max(
    options.decimals ?? options.maxDecimals ?? fallbackDecimals[1],
    minimumFractionDigits,
  );
  return {
    minimumFractionDigits,
    maximumFractionDigits,
    roundingMode: "halfExpand",
    signDisplay: options.signDisplay ?? "auto",
    useGrouping: options.grouping ?? true,
  };
}

/** A plain number: formatNumber("1234.5", { decimals: 2 }) is "1,234.50" in en-CA. */
export function formatNumber(value: NumericInput, options: NumberFormatOptions = {}): string {
  const decimal = Decimal.tryFrom(value);
  if (decimal === null) return MISSING_VALUE;
  return formatParts(decimal, options.locale ?? defaultLocale(), toIntlOptions(options, [0, 3]));
}

/** A whole number with grouping: formatInteger(12345) is "12,345". */
export function formatInteger(value: NumericInput, options: Omit<NumberFormatOptions, "decimals"> = {}): string {
  return formatNumber(value, { ...options, decimals: 0 });
}

/** Grams at the balance resolution (3 dp = 1 mg) without the unit: "12.346". */
export function formatGrams(value: NumericInput, options: NumberFormatOptions = {}): string {
  return formatNumber(value, { decimals: 3, ...options });
}

/** Grams with the unit, for plain-text contexts: "12.346 g". In UI, prefer <Quantity unit="g">. */
export function formatMass(value: NumericInput, options: NumberFormatOptions = {}): string {
  const number = formatGrams(value, options);
  return number === MISSING_VALUE ? number : `${number}\u00a0g`;
}

export interface PercentFormatOptions extends NumberFormatOptions {
  /** "fraction" when the input is 0–1 (the API's default for fractions); "percent" for *_pct fields. */
  from?: "fraction" | "percent";
}

/** Percentage number without the sign: formatPercentNumber("0.15", { from: "fraction" }) is "15.00". */
export function formatPercentNumber(value: NumericInput, options: PercentFormatOptions = {}): string {
  const decimal = Decimal.tryFrom(value);
  if (decimal === null) return MISSING_VALUE;
  const percent = options.from === "fraction" ? decimal.shift(2) : decimal;
  return formatParts(percent, options.locale ?? defaultLocale(), toIntlOptions(options, [2, 2]));
}

/** "15.00 %" (with a no-break space, per the typography rules). */
export function formatPercent(value: NumericInput, options: PercentFormatOptions = {}): string {
  const number = formatPercentNumber(value, options);
  return number === MISSING_VALUE ? number : `${number}\u00a0%`;
}

/** A 0–1 fraction as a percentage: formatFractionAsPercent("0.15") is "15.00 %". */
export function formatFractionAsPercent(fraction: NumericInput, options: NumberFormatOptions = {}): string {
  return formatPercent(fraction, { ...options, from: "fraction" });
}

/**
 * ISO 4217 minor-unit exponents that differ from 2. Mirrors backend/common/money.py so that
 * `price_minor` is interpreted exactly as the backend stored it.
 */
const MINOR_UNIT_EXPONENTS: Record<string, number> = {
  BHD: 3,
  CLP: 0,
  ISK: 0,
  JOD: 3,
  JPY: 0,
  KRW: 0,
  KWD: 3,
  OMR: 3,
  TND: 3,
  UGX: 0,
  VND: 0,
  XAF: 0,
  XOF: 0,
};

export function minorUnitExponent(currency: string): number {
  return MINOR_UNIT_EXPONENTS[currency.toUpperCase()] ?? 2;
}

/** Major-unit decimal string from minor units: minorToMajor(1240, "CAD") is "12.40". */
export function minorToMajor(minor: DecimalInput, currency: string): string {
  const exponent = minorUnitExponent(currency);
  return Decimal.from(minor).shift(-exponent).toFixed(exponent);
}

export interface MoneyFormatOptions {
  locale?: string;
  /** "symbol" gives CA$/US$ disambiguation outside the currency's home locale. */
  currencyDisplay?: "symbol" | "narrowSymbol" | "code" | "name";
  signDisplay?: "auto" | "always" | "exceptZero" | "never";
  /** Extra decimals for unit prices (e.g. per gram): the minor-unit exponent plus this. */
  extraDecimals?: number;
}

/** Money from integer minor units: formatMoney(1240, "CAD", { locale: "en-US" }) is "CA$12.40". */
export function formatMoney(minor: NumericInput, currency: string, options: MoneyFormatOptions = {}): string {
  const amount = Decimal.tryFrom(minor);
  if (amount === null || !currency) return MISSING_VALUE;
  const exponent = minorUnitExponent(currency);
  const decimals = exponent + (options.extraDecimals ?? 0);
  return formatParts(amount.shift(-exponent), options.locale ?? defaultLocale(), {
    style: "currency",
    currency: currency.toUpperCase(),
    currencyDisplay: options.currencyDisplay ?? "symbol",
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    roundingMode: "halfExpand",
    signDisplay: options.signDisplay ?? "auto",
  });
}

// Dates ------------------------------------------------------------------------------------------

const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Parses an API date. Date-only strings ("2026-10-12", Django DateField) are calendar dates in
 * the local time zone; `new Date("2026-10-12")` would be UTC midnight, the previous evening in
 * Vancouver.
 */
export function parseDate(value: string | number | Date | null | undefined): Date | null {
  if (value === null || value === undefined || value === "") return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === "string") {
    const dateOnly = DATE_ONLY_PATTERN.exec(value);
    if (dateOnly) return new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]));
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/** A local calendar date as the API and date inputs write it ("2026-10-13"). */
export function localIsoDate(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}

/** Today as a local calendar date ("2026-10-13"). Call from handlers or initializers. */
export function todayIso(): string {
  return localIsoDate(new Date());
}

const dateFormatterCache = new Map<string, Intl.DateTimeFormat>();

function dateFormatter(locale: string, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const key = `${locale}|${JSON.stringify(options)}`;
  let formatter = dateFormatterCache.get(key);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat(locale, options);
    dateFormatterCache.set(key, formatter);
  }
  return formatter;
}

export interface DateFormatOptions {
  dateStyle?: "short" | "medium" | "long";
  locale?: string;
  timeZone?: string;
}

/** "Oct 12, 2026" (medium, en-CA). */
export function formatDate(value: string | number | Date | null | undefined, options: DateFormatOptions = {}): string {
  const date = parseDate(value);
  if (date === null) return MISSING_VALUE;
  return dateFormatter(options.locale ?? defaultLocale(), {
    dateStyle: options.dateStyle ?? "medium",
    timeZone: options.timeZone,
  }).format(date);
}

/** "Oct 12, 2026, 11:56 p.m." (medium date, short time, en-CA). */
export function formatDateTime(
  value: string | number | Date | null | undefined,
  options: DateFormatOptions = {},
): string {
  const date = parseDate(value);
  if (date === null) return MISSING_VALUE;
  return dateFormatter(options.locale ?? defaultLocale(), {
    dateStyle: options.dateStyle ?? "medium",
    timeStyle: "short",
    timeZone: options.timeZone,
  }).format(date);
}

const RELATIVE_UNITS: Array<[Intl.RelativeTimeFormatUnit, number]> = [
  ["year", 365 * 24 * 3600],
  ["month", 30 * 24 * 3600],
  ["week", 7 * 24 * 3600],
  ["day", 24 * 3600],
  ["hour", 3600],
  ["minute", 60],
  ["second", 1],
];

/** "3 days ago", "in 2 hours", "now". Pair it with the absolute date in a Tooltip. */
export function formatRelativeTime(
  value: string | number | Date | null | undefined,
  options: { now?: Date; locale?: string } = {},
): string {
  const date = parseDate(value);
  if (date === null) return MISSING_VALUE;
  const seconds = Math.round((date.getTime() - (options.now ?? new Date()).getTime()) / 1000);
  const formatter = new Intl.RelativeTimeFormat(options.locale ?? defaultLocale(), { numeric: "auto" });
  if (Math.abs(seconds) < 45) return formatter.format(0, "second");
  for (const [unit, unitSeconds] of RELATIVE_UNITS) {
    if (Math.abs(seconds) >= unitSeconds || unit === "second") {
      return formatter.format(Math.round(seconds / unitSeconds), unit);
    }
  }
  return formatter.format(seconds, "second");
}

/** A short elapsed time: formatDuration(125_000, { locale: "en-CA" }) is "2 mins 5 secs". */
export function formatDuration(milliseconds: number | null | undefined, options: { locale?: string } = {}): string {
  if (milliseconds === null || milliseconds === undefined || !Number.isFinite(milliseconds)) return MISSING_VALUE;
  const totalSeconds = Math.max(0, Math.round(milliseconds / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const locale = options.locale ?? defaultLocale();
  const unit = (amount: number, name: "hour" | "minute" | "second") =>
    numberFormatter(locale, { style: "unit", unit: name, unitDisplay: "short" }).format(amount);
  if (hours > 0) return minutes > 0 ? `${unit(hours, "hour")} ${unit(minutes, "minute")}` : unit(hours, "hour");
  if (minutes > 0)
    return seconds > 0 ? `${unit(minutes, "minute")} ${unit(seconds, "second")}` : unit(minutes, "minute");
  return unit(seconds, "second");
}

/** "1 lot", "3 lots" (English plural rules; pass both forms). */
export function pluralize(count: number, one: string, other: string, options: { locale?: string } = {}): string {
  const form = new Intl.PluralRules(options.locale ?? defaultLocale()).select(count) === "one" ? one : other;
  return `${formatInteger(count, options)} ${form}`;
}
